import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  USER_CHAIN,
  USER_STORE,
} from "api/ApiPath";
import { StoreListRequestModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListRequestModel";
import { StoreListResponseModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListResponseModel";
import { PartnerManagementRequestModel } from "api/repositories/partnerManagement/model/PartnerManagementRequestModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { B2CUserInjection } from "features/b2cUserManagement/di/B2CUserInjection";
import { B2CUserActionRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserActionRequestEntity";
import { B2CUserRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserRequestEntity";
import { B2CUserResponseEntity } from "features/b2cUserManagement/domain/entities/B2CUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { UserTypeEnum } from "utils/enums/UserTypeEnum";
import { fetchFilterList } from "utils/format/FeatureFormat";
import { strings } from "utils/localizedStrings";

class StoreManagerInput implements Disposable {
  public fetchStoreManager = new BehaviorSubject<B2CUserRequestEntity>({});
  public fetchPartner = new BehaviorSubject<PartnerManagementRequestModel>({});
  public fetchStoreParent = new BehaviorSubject<{
    params?: B2CUserRequestEntity;
    type?: "parentList" | "parentModal";
    typeOfUser?: UserTypeEnum;
  }>({});
  public fetchStore = new BehaviorSubject<{
    params?: StoreListRequestModel;
    type?: "list" | "modal";
    typeOfUser?: UserTypeEnum;
  }>({});
  public storeLoading = new BehaviorSubject<string>("");
  public fetchDetail = new BehaviorSubject<B2CUserRequestEntity>({});
  public loading = new BehaviorSubject<boolean>(false);
  public modalLoading = new BehaviorSubject<boolean>(false);
  public fetchActions = new BehaviorSubject<{
    body?: B2CUserActionRequestEntity;
    action?: "create" | "update" | "delete";
  }>({});

  [Symbol.dispose](): void {
    this.fetchStoreManager.complete();
    this.fetchDetail.complete();
    this.fetchStoreParent.complete();
    this.fetchStore.complete();
    this.fetchPartner.complete();
    this.loading.complete();
    this.storeLoading.complete();
    this.modalLoading.complete();
  }
}

class StoreManagerOutput implements Disposable {
  public storeManagerList = new BehaviorSubject<Array<B2CUserResponseEntity>>(
    [],
  );
  public savedPartner = new BehaviorSubject<
    Array<PartnerManagementResponseModel>
  >([]);
  public savedStoreParent = new BehaviorSubject<Array<B2CUserResponseEntity>>(
    [],
  );
  public savedStoreParentForModal = new BehaviorSubject<
    Array<B2CUserResponseEntity>
  >([]);
  public savedStore = new BehaviorSubject<Array<StoreListResponseModel>>([]);
  public savedStoreForModal = new BehaviorSubject<
    Array<StoreListResponseModel>
  >([]);
  public savedDetail = new BehaviorSubject<B2CUserResponseEntity>({});
  public isUpdateSuccess = new BehaviorSubject<boolean>(false);
  public onActionsSuccess = new BehaviorSubject<string>("");
  public isError = new BehaviorSubject<Error>({});
  public paging = new BehaviorSubject<Paging>({});

  [Symbol.dispose](): void {
    this.storeManagerList.complete();
    this.isUpdateSuccess.complete();
    this.onActionsSuccess.complete();
    this.savedStoreParent.complete();
    this.savedStoreParentForModal.complete();
    this.savedDetail.complete();
    this.savedStore.complete();
    this.savedStoreForModal.complete();
    this.savedPartner.complete();
    this.isError.complete();
    this.paging.complete();
  }
}

@injectable()
export class StoreManagerViewModel extends BaseViewModel<
  StoreManagerInput,
  StoreManagerOutput
> {
  constructor() {
    super(new StoreManagerInput(), new StoreManagerOutput());
  }
  private useCase = B2CUserInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: B2CUserRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchStoreManager, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.useCase.getList(params, USER_STORE.LIST);
        if (res?.data) {
          this.output.storeManagerList.next(res?.data);
          this.output.paging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.isError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchStoreParent, async (props) => {
      if (props?.typeOfUser === UserTypeEnum.store) {
        this.input.storeLoading.next(props?.type);
        await fetchFilterList<B2CUserResponseEntity, B2CUserRequestEntity>({
          dataState: (value) =>
            props?.type === "parentList"
              ? this.output.savedStoreParent.next(value)
              : this.output.savedStoreParentForModal.next(value),
          path: USER_CHAIN.LIST,
          isForceRefresh: true,
          isNeedFeature: true,
          params: props?.params,
        })?.finally(() => this.input.storeLoading.next(""));
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      return await fetchFilterList<
        PartnerManagementResponseModel,
        PartnerManagementRequestModel
      >({
        dataState: (value) => this.output.savedPartner.next(value),
        path: PARTNER_MANAGEMENT.LIST,
        isForceRefresh: true,
        isNeedFeature: true,
        params,
        savedKey: CacheKeyEnum.b2cPartner,
      });
    });

    this.skipFirstInitial(this.input.fetchStore, async (props) => {
      if (props?.typeOfUser === UserTypeEnum.store) {
        this.input.storeLoading.next(props?.type);
        await fetchFilterList<StoreListResponseModel, StoreListRequestModel>({
          dataState: (value) =>
            props?.type === "list"
              ? this.output.savedStore.next(value)
              : this.output.savedStoreForModal.next(value),
          path: FILTER_FEATURE.STORE_LIST,
          isForceRefresh: true,
          isNeedFeature: false,
          params: props?.params,
        })?.finally(() => this.input.storeLoading.next(""));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.useCase.getDetail(params, USER_STORE.DETAIL);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.isError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body } = props;
      switch (action) {
        case "create":
          try {
            const res = await this.useCase?.createUser(body, USER_STORE.CREATE);
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.CreateSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.isError.next(error);
          }
          break;
        case "update":
          try {
            const res = await this.useCase?.updateUser(body, USER_STORE.UPDATE);
            if (res?.data) {
              this.output.isUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.isError.next(error);
          }
          break;
        case "delete":
          try {
            this.input.loading.next(true);
            const res = await this.useCase?.deleteUser(
              { phone: body?.phone },
              USER_STORE.DELETE,
            );
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.DeleteSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.isError.next(error);
          } finally {
            this.input.loading.next(false);
          }
          break;
      }
    });
  }
}
